/* eslint-disable  @typescript-eslint/no-explicit-any */ // AJB: remove
"use client";

import React, { useCallback, useEffect, useState } from "react";
import HomePageSearchRequest from "@/models/HomePageSearchRequest";
import { HomePageSearchResponse } from "@/models/HomePageSearchResponse";
import DEFAULT_SEARCH_REQUEST from "@/constants/DefaultSearchRequest";
import FiltersSection from "./sections/FiltersSection";
import ResultsSection from "./sections/ResultsSection";
import PaginationSection from "./sections/PaginationSection";
import { BuildUrl } from "@/actions/search/services/AdSearchUrlService";
import { useBodyBackdrop } from "@/components/search/useBodyBackdrop";
import { usePathname } from "next/navigation";
import { useRouter } from "nextjs-toploader/app";
import { Box, styled } from "@mui/material";
import { AdManagerProvider } from "@/app/layout/useAdManager";
import { SEARCH_ADS } from "./ads";
import AdModule from "@/components/shared/AdModule";
import { CreateAnalyticEvent } from "@/actions/details/CreateAnalyticEvent";
import PillButton from "@/app/shared/PillButton";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { SaveButton } from "@/components/search/SaveButton";
import { useDeviceNavigation } from "@/app/layout/useDeviceNavigation";
import { StickyBox } from "@/app/layout/StickyBox";
import {
  PAGE_MAX_WIDTH,
  Z_INDEX_FIXED_ACTIONS,
} from "@/app/layout/LayoutConstants";
import {
  BREAKPOINT_1_COLUMNS,
  BREAKPOINT_2_COLUMNS,
} from "./sections/SearchLayoutConstants";

type ResultsLoadingStatus = "pending" | "loading" | "loaded";

const LARGE_COLUMN_WIDTH = "300px";
const REMAINING_COLUMN_WIDTH = "minmax(0, 1fr)";

const MobileSearchActionsContainer = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: "none",
    gap: 1,
    width: "100%",
    justifyContent: "center",
    [theme.breakpoints.down(BREAKPOINT_1_COLUMNS)]: {
      display: "flex",
    },
  }),
);

function MobileSearchActions({
  isFilterFullScreen,
  setIsFilterFullScreen,
  searchPath,
  searchResponse,
}: {
  isFilterFullScreen: boolean;
  setIsFilterFullScreen: (isFullScreen: boolean) => void;
  searchPath: string;
  searchResponse: HomePageSearchResponse;
}) {
  return (
    <>
      <SaveButton type="SEARCH" currentKey={searchPath} />
      <PillButton
        primary
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          ".MuiSvgIcon-root": {
            fontSize: "120%",
          },
        }}
        onClick={() => setIsFilterFullScreen(!isFilterFullScreen)}
      >
        {isFilterFullScreen ? (
          <>
            <CheckRoundedIcon />
            Show {searchResponse.totalResults} Results
          </>
        ) : (
          <>
            <TuneRoundedIcon />
            Filters
          </>
        )}
      </PillButton>
    </>
  );
}

function ForSaleBody({
  searchPath,
  searchRequest,
  setSearchRequest,
  searchResponse,
  isFilterFullScreen,
  setIsFilterFullScreen,
  loadingStatus,
  featuredResponse,
}: {
  searchPath: string;
  searchRequest: HomePageSearchRequest;
  setSearchRequest: (
    searchRequest: HomePageSearchRequest,
    skipRefresh?: boolean,
  ) => void;
  searchResponse: HomePageSearchResponse;
  isFilterFullScreen: boolean;
  setIsFilterFullScreen: (isFullScreen: boolean) => void;
  loadingStatus: ResultsLoadingStatus;
  featuredResponse: HomePageSearchResponse;
}) {
  const handleSearchRequestChange = (updates: any, skipRefresh?: boolean) => {
    const isResetting = updates.vertical;
    const newSearchRequest = {
      ...(isResetting ? DEFAULT_SEARCH_REQUEST : searchRequest),
      ...updates,
      page: updates.page || 1,
    };
    setSearchRequest(newSearchRequest, skipRefresh);

    if (
      typeof updates.page !== "undefined" ||
      typeof updates.sortBy !== "undefined"
    ) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const filtersFixedStyling = {
    zIndex: Z_INDEX_FIXED_ACTIONS,
    position: "fixed",
    top: isFilterFullScreen ? 0 : "100vh",
    left: 0,
    right: 0,
    transition: "top 0.3s ease-in-out",
    height: "100vh",
    overflowY: "auto",
    backgroundColor: "white",
    paddingBottom: 16,
  };

  return (
    <>
      {searchResponse && (
        <Box
          sx={{
            backgroundColor: "grey.100",
          }}
        >
          <Box
            sx={(theme) => ({
              display: "grid",
              gridTemplateColumns: `${LARGE_COLUMN_WIDTH} ${REMAINING_COLUMN_WIDTH} ${LARGE_COLUMN_WIDTH}`,
              maxWidth: PAGE_MAX_WIDTH,
              margin: "0 auto",
              gap: 4,
              paddingTop: 3,
              [theme.breakpoints.down(PAGE_MAX_WIDTH)]: {
                paddingLeft: 2,
                paddingRight: 2,
              },
              [theme.breakpoints.down(BREAKPOINT_2_COLUMNS)]: {
                gridTemplateColumns: `${LARGE_COLUMN_WIDTH} ${REMAINING_COLUMN_WIDTH}`,
              },
              [theme.breakpoints.down(BREAKPOINT_1_COLUMNS)]: {
                gridTemplateColumns: REMAINING_COLUMN_WIDTH,
                padding: 0,
              },
            })}
          >
            <Box
              sx={(theme) => ({
                gridRow: 1,
                gridColumn: 1,
                [theme.breakpoints.down(BREAKPOINT_1_COLUMNS)]:
                  filtersFixedStyling,
                ...(isFilterFullScreen ? filtersFixedStyling : {}),
              })}
            >
              <StickyBox noPadding showScrollbar>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    paddingBottom: 4,
                  }}
                >
                  <FiltersSection
                    searchResponse={searchResponse}
                    searchRequest={searchRequest}
                    onSearchRequestChange={handleSearchRequestChange}
                    isLoading={loadingStatus === "loading"}
                    searchPath={searchPath}
                  />
                </Box>
              </StickyBox>
            </Box>
            <Box
              sx={(theme) => ({
                backgroundColor: "white",
                padding: 4,
                borderRadius: 10,
                gridRow: 1,
                gridColumn: 2,
                [theme.breakpoints.down(BREAKPOINT_1_COLUMNS)]: {
                  gridColumn: 1,
                  borderRadius: 0,
                },
                [theme.breakpoints.down("sm")]: {
                  padding: theme.spacing(2, 1),
                },
                display: "flex",
                flexDirection: "column",
              })}
            >
              <ResultsSection
                searchResponse={searchResponse}
                searchRequest={searchRequest}
                handleSearchRequestChange={handleSearchRequestChange}
                featuredResponse={featuredResponse}
              />
              <Box
                sx={{
                  paddingTop: 8,
                  marginTop: "auto",
                }}
              >
                <PaginationSection
                  searchRequest={searchRequest}
                  searchResponse={searchResponse}
                  handleSearchRequestChange={handleSearchRequestChange}
                />
              </Box>

              <MobileSearchActionsContainer
                sx={{
                  position: "fixed",
                  bottom: 0,
                  right: 0,
                  padding: 1,
                  zIndex: Z_INDEX_FIXED_ACTIONS,
                  backgroundColor: "white",
                  boxShadow: "0 -8px 6px -6px rgba(0, 0, 0, 0.3)",
                }}
              >
                <MobileSearchActions
                  isFilterFullScreen={isFilterFullScreen}
                  setIsFilterFullScreen={setIsFilterFullScreen}
                  searchPath={searchPath}
                  searchResponse={searchResponse}
                />
              </MobileSearchActionsContainer>
            </Box>
            {searchResponse?.results.ads.length >= 8 && (
              <Box
                sx={(theme) => ({
                  gridRow: 1,
                  gridColumn: 3,
                  gap: 4,
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "grey.100",
                  alignItems: "start",
                  [theme.breakpoints.down(BREAKPOINT_2_COLUMNS)]: {
                    gridRow: 2,
                    gridColumn: "1 / span 2",
                    alignItems: "center",
                  },
                  [theme.breakpoints.down(BREAKPOINT_1_COLUMNS)]: {
                    gridColumn: "1",
                  },
                })}
              >
                <AdModule config={SEARCH_ADS.Horsezone_Search_Side_1} />
                <AdModule config={SEARCH_ADS.Horsezone_Search_Side_2} />
                <AdModule config={SEARCH_ADS.Horsezone_Search_Side_3} />
              </Box>
            )}
            <Box
              sx={(theme) => ({
                gridRow: 2,
                gridColumn: 2,
                paddingBottom: 4,
                [theme.breakpoints.down(PAGE_MAX_WIDTH)]: {
                  gridRow: 6,
                },
                [theme.breakpoints.down(1400)]: {
                  gridColumn: "1 / span 3",
                },
                [theme.breakpoints.down(BREAKPOINT_2_COLUMNS)]: {
                  gridColumn: "1 / span 2",
                  gridRow: 3,
                },
                [theme.breakpoints.down(BREAKPOINT_1_COLUMNS)]: {
                  gridColumn: "1",
                },
              })}
            >
              <AdModule config={SEARCH_ADS.Horsezone_Search_Bottom} />
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
}

function ForSalePageContainer({
  searchPath,
  initialSearchRequest,
  initialSearchResponse,
  featuredResponse,
}: {
  searchPath: string;
  initialSearchRequest: HomePageSearchRequest;
  initialSearchResponse: HomePageSearchResponse;
  featuredResponse: HomePageSearchResponse;
}) {
  const [searchRequest, setSearchRequest] =
    useState<HomePageSearchRequest>(initialSearchRequest);
  const [loadingStatus, setLoadingStatus] =
    useState<ResultsLoadingStatus>("loaded");
  const {
    isFullScreen: isFilterFullScreen,
    setIsFullScreen: setIsFilterFullScreen,
  } = useDeviceNavigation();
  const router = useRouter();
  const pathname = usePathname();
  const { backdropOpen } = useBodyBackdrop();

  const loadNewResults = useCallback(
    (newUrl: string) => {
      setLoadingStatus("loading");
      router.push(newUrl, undefined); // AJB: handle scroll here?
    },
    [router],
  );

  useEffect(() => {
    const newUrl = BuildUrl(searchRequest);
    const urlHasChanged = newUrl !== pathname;
    const isLoadingOneChanges = loadingStatus === "loaded" && urlHasChanged;
    const isLoadingMultipleChanges =
      loadingStatus === "pending" && !backdropOpen;
    const isFinishedLoading = loadingStatus === "loading" && !urlHasChanged;
    const isLoadingChanges = isLoadingOneChanges || isLoadingMultipleChanges;

    if (isLoadingChanges) {
      loadNewResults(newUrl);
    } else if (isFinishedLoading) {
      setLoadingStatus("loaded");
    }
  }, [backdropOpen, loadingStatus, searchRequest, pathname, loadNewResults]);

  return (
    <ForSaleBody
      searchPath={searchPath}
      searchRequest={searchRequest}
      setSearchRequest={(newSearchRequest, skipRefresh) => {
        if (skipRefresh) {
          setLoadingStatus("pending");
        }
        setSearchRequest(newSearchRequest);
      }}
      searchResponse={initialSearchResponse}
      isFilterFullScreen={isFilterFullScreen}
      setIsFilterFullScreen={setIsFilterFullScreen}
      loadingStatus={loadingStatus}
      featuredResponse={featuredResponse}
    />
  );
}

export default function ForSalePage({
  searchPath,
  initialSearchRequest,
  initialSearchResponse,
  featuredResponse,
}: {
  searchPath: string;
  initialSearchRequest: HomePageSearchRequest;
  initialSearchResponse: HomePageSearchResponse;
  featuredResponse: HomePageSearchResponse;
}) {
  useEffect(() => {
    CreateAnalyticEvent({
      event: {
        adIds: initialSearchResponse.results.ads.map((ad) => ad.id),
        eventType: "AD_IMPRESSIONS#SEARCH#ALL_RESULTS",
      },
    });
  }, [initialSearchResponse]);

  return (
    <AdManagerProvider ads={SEARCH_ADS}>
      <ForSalePageContainer
        searchPath={searchPath}
        initialSearchRequest={initialSearchRequest}
        initialSearchResponse={initialSearchResponse}
        featuredResponse={featuredResponse}
      />
    </AdManagerProvider>
  );
}
